import { Bike, User as UserIcon } from "lucide-react";
import { LOGO_URL } from "../lib/format";

/**
 * Race Card — the "trading card" identity of a Speed Freaks rider.
 * Variants:
 *  - variant="podium"    : compact card used on ranking podium (1st/2nd/3rd)
 *  - variant="profile"   : big card used inside the Passaporte page
 *  - variant="instagram" : 9:16 story-ready snapshot (fullscreen usage)
 * Props: rider = { name, motorcycle_model, photo_url, rides_count }, accent, position?
 */
export default function RaceCard({
  rider,
  variant = "profile",
  position,
  accent = "#00E5FF",
  testId = "race-card",
}) {
  if (variant === "instagram") {
    return <InstagramCard rider={rider} accent={accent} testId={testId} />;
  }
  return (
    <StandardCard
      rider={rider}
      variant={variant}
      position={position}
      accent={accent}
      testId={testId}
    />
  );
}

/* --------------------------------------------------------
   Standard card (podium + profile)
-------------------------------------------------------- */
function StandardCard({ rider, variant, position, accent, testId }) {
  const photo = (rider?.photo_url || "").trim();
  const name = rider?.name || rider?.rider_name || "Piloto Speed Freaks";
  const moto = rider?.motorcycle_model || rider?.motorcycles?.[0] || "";
  const rides = rider?.rides_count ?? 0;

  const isFirst = position === 1;
  const isPodium = variant === "podium";

  // 1st place gets a much stronger multi-layer glow
  const glow = isFirst
    ? "0 0 48px rgba(0,229,255,0.6), 0 0 100px rgba(0,229,255,0.35), 0 0 160px rgba(57,255,20,0.25)"
    : isPodium
    ? `0 0 18px ${accent}30`
    : `0 0 20px ${accent}33`;

  return (
    <div
      className="relative border bg-[#0B0D14] overflow-hidden transition-transform w-full"
      style={{
        borderColor: isFirst ? `${accent}CC` : `${accent}55`,
        boxShadow: glow,
        borderWidth: isFirst ? 2 : 1,
      }}
      data-testid={testId}
    >
      {/* Top strip with group logo */}
      <div
        className="flex items-center justify-between px-3 py-2 border-b"
        style={{
          borderColor: `${accent}33`,
          background: `linear-gradient(90deg, ${accent}12, transparent)`,
        }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <img
            src={LOGO_URL}
            alt="Speed Freaks"
            className={`${isFirst ? "h-7 w-7" : "h-6 w-6"} object-contain shrink-0`}
          />
          <span
            className={`label-caps truncate ${isFirst ? "text-[11px]" : ""}`}
            style={{ color: accent, letterSpacing: "0.25em" }}
          >
            Speed Freaks
          </span>
        </div>
        {position ? (
          <span
            className={`font-display ${isFirst ? "text-2xl" : "text-lg"}`}
            style={{
              color: accent,
              textShadow: isFirst ? `0 0 14px ${accent}` : "none",
            }}
            data-testid={`race-card-position-${position}`}
          >
            #{position}
          </span>
        ) : null}
      </div>

      {/* Photo area */}
      <div
        className="relative w-full"
        style={{
          aspectRatio: "1/1",
          background:
            "radial-gradient(circle at 30% 20%, rgba(0,229,255,0.12), transparent 60%), radial-gradient(circle at 80% 90%, rgba(57,255,20,0.08), transparent 55%), #090A0F",
        }}
      >
        {photo ? (
          <img
            src={photo}
            alt={name}
            loading="eager"
            decoding="async"
            referrerPolicy="no-referrer"
            crossOrigin="anonymous"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ imageRendering: "auto" }}
            onError={(e) => {
              // Google/Instagram sometimes reject cross-origin; retry without CORS.
              const img = e.currentTarget;
              if (img.dataset.retry !== "1") {
                img.dataset.retry = "1";
                img.removeAttribute("crossorigin");
                img.src = photo + (photo.includes("?") ? "&" : "?") + "_r=" + Date.now();
              } else {
                img.style.display = "none";
              }
            }}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <UserIcon size={isFirst ? 80 : 64} className="text-white/20" />
          </div>
        )}
      </div>

      {/* Identity strip — name on the left, rides badge aligned bottom-right */}
      <div className={isFirst ? "px-4 py-4" : "px-4 py-3"}>
        <div className="flex items-end justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div
              className={`text-white font-display uppercase tracking-wide truncate ${
                isFirst ? "text-2xl sm:text-3xl" : "text-lg sm:text-xl"
              }`}
              data-testid="race-card-name"
            >
              {name}
            </div>
            {moto ? (
              <div className="mt-1 flex items-center gap-2 text-white/70 text-sm truncate">
                <Bike size={14} style={{ color: accent }} />
                <span className="truncate" data-testid="race-card-moto">
                  {moto}
                </span>
              </div>
            ) : null}
          </div>

          {/* Rides badge — bottom-right, aligned with the name baseline */}
          <div
            className="shrink-0 px-2.5 py-1.5 border text-right leading-none"
            style={{
              borderColor: `${accent}CC`,
              background: "rgba(0,0,0,0.55)",
              boxShadow: `0 0 12px ${accent}22`,
            }}
          >
            <div
              className={`font-display ${isFirst ? "text-3xl" : "text-xl"}`}
              style={{ color: accent, textShadow: `0 0 10px ${accent}` }}
              data-testid="race-card-rides-count"
            >
              {rides}
            </div>
            <div className="label-caps text-[8px] mt-0.5 text-white/75">
              {rides === 1 ? "rolê" : "rolês"}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* --------------------------------------------------------
   Instagram Story card — 9:16, poster-style, NO chrome
-------------------------------------------------------- */
function InstagramCard({ rider, accent, testId }) {
  const photo = (rider?.photo_url || "").trim();
  const name = rider?.name || rider?.rider_name || "Piloto Speed Freaks";
  const moto = rider?.motorcycle_model || rider?.motorcycles?.[0] || "";
  const rides = rider?.rides_count ?? 0;

  return (
    <div
      className="relative w-full overflow-hidden bg-black"
      style={{
        aspectRatio: "9/16",
        boxShadow:
          "0 0 60px rgba(0,229,255,0.35), 0 0 120px rgba(57,255,20,0.18)",
      }}
      data-testid={testId}
    >
      {/* Full-bleed photo background */}
      {photo ? (
        <img
          src={photo}
          alt={name}
          loading="eager"
          decoding="async"
          referrerPolicy="no-referrer"
          crossOrigin="anonymous"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ imageRendering: "auto" }}
          onError={(e) => {
            const img = e.currentTarget;
            if (img.dataset.retry !== "1") {
              img.dataset.retry = "1";
              img.removeAttribute("crossorigin");
              img.src = photo + (photo.includes("?") ? "&" : "?") + "_r=" + Date.now();
            } else {
              img.style.display = "none";
            }
          }}
        />
      ) : (
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(circle at 30% 25%, rgba(0,229,255,0.25), transparent 55%), radial-gradient(circle at 75% 80%, rgba(57,255,20,0.18), transparent 55%), #05060A",
          }}
        >
          <div className="absolute inset-0 flex items-center justify-center">
            <UserIcon size={140} className="text-white/15" />
          </div>
        </div>
      )}

      {/* Vignette + top+bottom gradients for legibility */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 22%, rgba(0,0,0,0) 55%, rgba(0,0,0,0.9) 100%)",
        }}
      />

      {/* Top: Transparent Speed Freaks logo lockup */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-center gap-3 pt-6 pb-4">
        <img
          src={LOGO_URL}
          alt="Speed Freaks"
          className="h-14 w-14 object-contain"
          style={{
            opacity: 0.85,
            filter: "drop-shadow(0 0 12px rgba(0,229,255,0.55))",
          }}
        />
        <div className="leading-none">
          <div
            className="font-display text-2xl tracking-wide"
            style={{
              color: "#FFFFFF",
              opacity: 0.92,
              textShadow: "0 2px 12px rgba(0,0,0,0.9)",
            }}
          >
            SPEED<span style={{ color: accent }}>FREAKS</span>
          </div>
          <div
            className="label-caps text-[10px] mt-1"
            style={{ color: "rgba(255,255,255,0.7)", letterSpacing: "0.35em" }}
          >
            Race Team
          </div>
        </div>
      </div>

      {/* Elite seal — scalloped stamp/badge (SVG), bottom-right corner */}
      <EliteSeal rides={rides} accent={accent} size={124} />

      {/* Bottom: rider identity — big poster typography (leaves room for the seal on the right) */}
      <div className="absolute left-0 right-0 bottom-0 px-6 pb-8 pr-36">
        <div
          className="label-caps mb-2"
          style={{ color: accent, letterSpacing: "0.3em", fontSize: 11 }}
        >
          Piloto Elite
        </div>
        <div
          className="font-display uppercase text-white leading-[0.95]"
          style={{
            fontSize: "clamp(2.25rem, 8vw, 3.5rem)",
            textShadow: "0 3px 18px rgba(0,0,0,0.9)",
            letterSpacing: "0.01em",
          }}
          data-testid="insta-name"
        >
          {name}
        </div>
        {moto ? (
          <div
            className="mt-3 flex items-center gap-2 text-white/95"
            style={{ textShadow: "0 2px 10px rgba(0,0,0,0.9)" }}
          >
            <Bike size={18} style={{ color: accent }} />
            <span
              className="font-body text-lg sm:text-xl truncate"
              data-testid="insta-moto"
            >
              {moto}
            </span>
          </div>
        ) : null}
        <div
          className="mt-3 h-[2px] w-16"
          style={{
            background: `linear-gradient(90deg, ${accent}, transparent)`,
            boxShadow: `0 0 12px ${accent}`,
          }}
        />
      </div>
    </div>
  );
}


/* --------------------------------------------------------
   Elite Seal — SVG scalloped stamp (poster-style)
-------------------------------------------------------- */
function EliteSeal({ rides, accent, size = 120 }) {
  // Build a 24-notch scalloped polygon (looks like a wax seal / diploma stamp).
  const N = 24;
  const cx = 50;
  const cy = 50;
  const rOuter = 49;
  const rInner = 42;
  const pts = [];
  for (let i = 0; i < N * 2; i++) {
    const angle = (i * Math.PI) / N - Math.PI / 2;
    const r = i % 2 === 0 ? rOuter : rInner;
    pts.push(
      `${(cx + r * Math.cos(angle)).toFixed(2)},${(cy + r * Math.sin(angle)).toFixed(2)}`
    );
  }
  return (
    <div
      className="absolute bottom-6 right-5 z-10 flex items-center justify-center"
      style={{
        width: size,
        height: size,
        transform: "rotate(-6deg)",
        filter: `drop-shadow(0 0 18px ${accent}AA)`,
      }}
      data-testid="elite-seal"
    >
      <svg
        viewBox="0 0 100 100"
        width={size}
        height={size}
        className="absolute inset-0"
      >
        <polygon
          points={pts.join(" ")}
          fill="rgba(0,0,0,0.82)"
          stroke={accent}
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        <circle
          cx="50"
          cy="50"
          r="34"
          fill="none"
          stroke={accent}
          strokeWidth="0.9"
          opacity="0.7"
        />
        <circle
          cx="50"
          cy="50"
          r="37"
          fill="none"
          stroke={accent}
          strokeWidth="0.4"
          opacity="0.4"
        />
      </svg>
      <div className="relative flex flex-col items-center justify-center leading-none">
        <div
          className="font-display"
          style={{
            fontSize: 34,
            color: accent,
            textShadow: `0 0 10px ${accent}`,
          }}
        >
          {rides}
        </div>
        <div
          className="label-caps mt-0.5"
          style={{ color: "#fff", fontSize: 8, letterSpacing: "0.22em" }}
        >
          {rides === 1 ? "rolê" : "rolês"}
        </div>
        <div
          className="mt-1.5 label-caps text-center"
          style={{
            color: accent,
            fontSize: 7.5,
            letterSpacing: "0.18em",
            lineHeight: 1.15,
          }}
        >
          Equipe
          <br />
          de Elite
        </div>
      </div>
    </div>
  );
}
